import { 
  Card, 
  CardHeader, 
  Divider, 
  Grid, 
  Paper, 
  SxProps, 
  Theme, 
  Typography,
  IconButton,
  Button,
  Box,
  Tooltip
} from "@mui/material";
import React, { useEffect, useState } from "react";
import WSBackButton from "../../Components/BackButton/BackButton";
import { getComponentProps, unRegisterComponent, updateOrRegisterComponent } from "../../StateManagement/RegisterComponent";
import { WSHeaders } from "../../commonFunctions/HeaderConfig/HeaderConfig";

interface HeaderAction {
  type: 'button' | 'icon';
  label?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  variant?: 'text' | 'outlined' | 'contained';
  tooltip?: string;
  sx?: SxProps<Theme>;
}

interface IHeaderProps {
  title: string;
  type?: 'simple' | 'card' | 'light';
  children?: React.ReactNode;
  isBackButton?: boolean;
  url?: string;
  id?: WSHeaders | any;
  isDivider?: boolean;
  headerSx?: SxProps<Theme>;
  actions?: HeaderAction[]; // New prop for actions
  actionPosition?: 'left' | 'right'; // Position for actions
}

export const Header: React.FC<IHeaderProps> = React.memo((props) => {
  const [newUrl, setUrl] = useState<string | undefined>(props?.url);
  const [hasBackButton, setBackButton] = useState<boolean | undefined>(props?.isBackButton);

  // Register the component with initial props
  useEffect(() => {
    updateOrRegisterComponent(props?.id, props);
  }, []);
  
  useEffect(() => () => {
    unRegisterComponent(props?.id);
  }, []);
  
  // Handle updates from other components
  useEffect(() => {
    const propsed = getComponentProps(props?.id);
    if (propsed) {
      setUrl(propsed.url);
      setBackButton(propsed.isBackButton);
    }
  }, [props?.id]);

  // Render action buttons/icons
  const renderActions = () => {
    if (!props.actions || props.actions.length === 0) return null;

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        ml: props.actionPosition === 'left' ? 0 : 'auto',
        mr: props.actionPosition === 'right' ? 0 : 'auto'
      }}>
        {props.actions.map((action, index) => {
          const actionElement = action.type === 'icon' ? (
            <Tooltip key={index} title={action.tooltip || ''}>
              <IconButton
                onClick={action.onClick}
                color={action.color || 'primary'}
                sx={action.sx}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              key={index}
              variant={action.variant || 'contained'}
              color={action.color || 'primary'}
              onClick={action.onClick}
              startIcon={action.icon}
              sx={action.sx}
            >
              {action.label}
            </Button>
          );

          return actionElement;
        })}
      </Box>
    );
  };

  return (
    <>
      {props?.type === 'card' && (
        <Paper style={{ backgroundColor: '#F8F8FF' }}>
          <Card style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '5px',
            width: '100%',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #ffffff'
          }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography id={props?.id} variant="h6" style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '1.25rem'
                  }}>
                    {props?.title}
                  </Typography>
                </Box>
              }
              action={renderActions()}
              style={{ padding: '20px' }}
            />
            {props?.children}
          </Card>
        </Paper>
      )}
      
      {props?.type === 'simple' && (
        <Grid item container spacing={2} sx={{...props?.headerSx, marginLeft: 0, width: '100%'}} >
          <Grid item sm={12} sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            height: "100%",
            "&.MuiGrid-item": { paddingTop: 0, height: 50 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {hasBackButton && <WSBackButton id="btnScheduleBack" url={newUrl} />}
              <Typography className="header-text">
                {props?.title}
              </Typography>
            </Box>
            {renderActions()}
          </Grid>
        </Grid>
      )}
      
      {props?.type === 'light' && (
        <Grid sx={{ 
          backgroundColor: "white", 
          borderRadius: 2, 
          p: 2, 
          boxShadow: 1,
          marginBottom: 0,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ color: "#7D79C2" }}>
            {props?.title}
          </Typography>
          {renderActions()}
        </Grid>
      )}
      
      {!props?.isDivider && <br/>}
      {props?.isDivider && (
        <Grid item sm={12} xs={12} style={{ marginTop: 10 }}>
          <Divider />
          <br />
        </Grid>
      )}
    </>
  );
});